import {
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  LineColorPicker,
  Switch,
  useDialogExitWarning,
} from '@ais-ui/ui-core';
import React, { useCallback } from 'react';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { useLocalization } from '@ais-ui/locale-management';
import { useSaveShiplist } from './useSaveShiplist';
import { ShiplistConfirmationDialog } from './ShiplistConfirmationDialog';
import { useMutateShiplistContext } from './MutateShiplistContext';
import { AddShipsToListFormField } from './AddShipsToListFormField';
import { useOwnershipCheck } from '../../../utils/useOwnershipCheck';

const SaveShiplistForm = () => {
  const { messages } = useLocalization();
  const { shiplist, form, action } = useMutateShiplistContext();
  const { isOwner } = useOwnershipCheck();

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shiplistsSaveShiplistNameLabel} *</FormLabel>
              <InfoIconTooltip>
                {messages.shiplistsSaveNameOfShiplistTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                disabled={shiplist !== undefined}
                id="shiplist-name"
                type="text"
                placeholder={messages.shiplistsSaveShiplistNamePlaceholder}
                autoFocus={shiplist === undefined}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        key="color"
        control={form.control}
        name="color"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shiplistsSaveColorLabel} *</FormLabel>
              <InfoIconTooltip>
                {messages.shiplistsSaveColorPlaceholder}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <LineColorPicker
                background={field.value}
                setBackground={(color) => {
                  form.setValue('color', color);
                }}
                className="flex-shrink-0 flex-grow-0 w-12 p-0"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        key="selectedGroups"
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shiplistsSaveShareLabel}</FormLabel>
              <InfoIconTooltip>
                {messages.shiplistsSaveShareTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect
                {...field}
                disabled={action === 'edit' && !isOwner(shiplist)}
                disabledReason={
                  messages.genericNotOwnerDisabledText ||
                  'You must be the owner to share this object'
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        key="useFillShipList"
        control={form.control}
        name="useFillShipList"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="!mt-0">
                  {messages.shiplistsSaveFillShipListLabel}
                </FormLabel>
              </div>
              <InfoIconTooltip>
                {messages.shiplistsSaveFillShipListTooltip}
              </InfoIconTooltip>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <AddShipsToListFormField></AddShipsToListFormField>
    </>
  );
};

export function SaveShiplistDialog() {
  const { messages } = useLocalization();

  const { showDialog, setShowDialog, action, isLoading, isSubmitting, form } =
    useMutateShiplistContext();

  const {
    onSubmit,
    handleCancel,
    handleConfirm,
    showConfirmDialog,
    setShowConfirmDialog,
  } = useSaveShiplist();
  const [dialogTitle, dialogDescription] =
    action === 'edit'
      ? [messages.shiplistsSaveEditTitle, messages.shiplistsSaveEditSubtitle]
      : [
          messages.shiplistsSaveCreateTitle,
          messages.shiplistsSaveCreateSubtitle,
        ];

  const handleExitConfirm = useCallback(() => {
    setShowDialog(false);
  }, [setShowDialog]);

  const { handleClose, ExitWarningDialog } = useDialogExitWarning({
    onConfirm: handleExitConfirm,
    isDirty: form.formState.isDirty,
    title: messages.shiplistsWarningTitle || 'Discard shiplist changes?',
    description:
      messages.shiplistsWarningDescription ||
      'Your shiplist configuration will be lost. Are you sure you want to close?',
  });

  return (
    <Form {...form}>
      <DialogForm
        showDialog={showDialog}
        onOpenChange={setShowDialog}
        size="lg"
        onClose={handleClose}
      >
        <DialogForm.Header>
          <DialogForm.Title>{dialogTitle}</DialogForm.Title>
          <DialogForm.Description>{dialogDescription}</DialogForm.Description>
        </DialogForm.Header>
        <DialogForm.Body
          isLoading={isLoading}
          hasLoaded={!isLoading}
          observeResize={false}
        >
          <SaveShiplistForm />
        </DialogForm.Body>
        <DialogForm.Footer>
          <DialogForm.Cancel>{messages.genericCancel}</DialogForm.Cancel>
          <DialogForm.Submit
            id="shiplist-submit-button"
            disabled={isSubmitting}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </DialogForm.Submit>
        </DialogForm.Footer>
      </DialogForm>
      <ShiplistConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <ExitWarningDialog />
    </Form>
  );
}
